// Persistence for the development flags the user has switched on (see
// FlagContext.jsx) — the web stand-in for the app's AsyncStorage 'flags' item. A
// JSON array of flag names under one localStorage key, so the choice survives a
// reload, a tab close and a browser restart. Like the app it is NOT cleared on
// sign-out: what is stored is a preference, and whether a stored flag has any
// effect is decided at read time against the account's eligible groups
// (FlagContext intersects the two), so a flag a user may no longer use is simply
// inert. Independent of the darwin_core store and the Cognito token store for the
// same reasons selectedHome.js is: it is a non-sensitive UI preference.
//
// Every access is guarded the same way as selectedHome.js: localStorage can be
// absent (non-browser contexts), throw on access (browser policy, private mode) or
// hold something unparseable (a hand-edited value). A persistence failure must
// never break the menu, so reads fall back to "no flags" and writes fail silently.

const STORAGE_KEY = 'halo.web.flags';

function getStore() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Accessing localStorage can itself throw (e.g. blocked by browser policy).
    return null;
  }
}

// The stored flag names, or an empty list if nothing usable is stored or storage
// is unavailable. Only an array of strings is trusted; anything else reads as
// nothing stored rather than being passed on.
export function getStoredFlags() {
  const store = getStore();
  if (!store) {
    return [];
  }
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((flag) => typeof flag === 'string')
      : [];
  } catch {
    return [];
  }
}

// Persists the given flag names. Returns true on success, false if they could
// not be persisted (the in-memory state still applies for this visit).
export function setStoredFlags(flags) {
  const store = getStore();
  if (!store) {
    return false;
  }
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(flags));
    return true;
  } catch {
    return false;
  }
}

// Removes any stored flags. Safe to call when nothing is stored.
export function clearStoredFlags() {
  const store = getStore();
  if (!store) {
    return;
  }
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Ignore: clearing a preference must never surface an error.
  }
}
